import { LogRocket } from '@cheqroom/analytics';
import type { ErrorEvent, EventHint, Integration } from '@sentry/core';
import * as Sentry from '@sentry/react';

const NON_TRACED_URLS = [
	'fonts.googleapis.com',
	'lr-in-prod.com',
	'logr-ingest.com',
	'segment.cheqroom.io',
	'typekit.net',
	'appcues.com',
	'userflow.com',
]
	.map((url) => url.replace(/\./g, '\\.'))
	.join('|');

const nonTracedUrls = new RegExp(NON_TRACED_URLS);

export type SentryInitContext = { nonTracedUrls: RegExp };

export type SentryInitOptions = {
	dsn: string;
	getIntegrations?: (integrations: Integration[], context: SentryInitContext) => Integration[];
	beforeSend?: (event: ErrorEvent, hint: EventHint) => ErrorEvent | PromiseLike<ErrorEvent | null> | null;
};

const defaultGetIntegrations: NonNullable<SentryInitOptions['getIntegrations']> = (integrations, { nonTracedUrls }) => [
	// Exclude Dedupe integration because it currently prevents
	// errors from being sent to Sentry because they are thrown twice
	...integrations.filter((integration: Integration) => integration.name !== 'Dedupe'),
	// The tracename is set in shell.js with the router:navigation:processing event
	Sentry.browserTracingIntegration({
		shouldCreateSpanForRequest: (url) => !nonTracedUrls.test(url),
	}),
];

function defaultBeforeSend(event: ErrorEvent, _hint: EventHint): ErrorEvent | null {
	// hint available for future use (e.g. originalException)
	void _hint;
	try {
		const logRocketSession = LogRocket.getSessionURL();
		if (logRocketSession !== null) {
			event.extra = { ...event.extra, 'LogRocket Session': logRocketSession };
		}
	} catch {
		// LogRocket not available (e.g. in portal)
	}
	return event;
}

export function initSentry(options: SentryInitOptions): void {
	const { dsn, getIntegrations = defaultGetIntegrations, beforeSend = defaultBeforeSend } = options;

	Sentry.init({
		dsn,
		environment: process.env.ENVIRONMENT || 'development',
		integrations: (integrations) => getIntegrations(integrations, { nonTracedUrls }),
		tracePropagationTargets: ['localhost', /^https:\/\/((?!utils\.)([^.]*)\.)?cheqroom\.(dev|com)/],
		tracesSampleRate: process.env.ENVIRONMENT === 'development' ? 1 : 0.05,
		release: process.env.BUILD_VERSION,
		ignoreErrors: ["Error: Could not find what you're looking for"],
		beforeSend,
	});
}

export default Sentry;
